import {
  createContext,
  type ReactElement,
  type ReactNode,
  useContext,
  useMemo,
} from 'react'

import { type MeQuery, useMeQuery } from '@/__generated__/hooks'

// Children only mount after the user is loaded, so consumers never see null.
type CurrentUser = MeQuery['me']

const CurrentUserContext = createContext<CurrentUser | null>(null)

export const useCurrentUser = (): CurrentUser => {
  const ctx = useContext(CurrentUserContext)
  if (!ctx) {
    throw new Error(
      'useCurrentUser must be used inside <CurrentUserProvider> (which only renders children after the user is loaded).'
    )
  }
  return ctx
}

type Props = { children: ReactNode; fallback?: ReactNode }

const CurrentUserProvider = ({ children, fallback = null }: Props) => {
  const { data, loading } = useMeQuery()
  const value = useMemo(() => data?.me ?? null, [data?.me])

  if (loading && !value) return (fallback ?? null) as ReactElement | null
  if (!value) return (fallback ?? null) as ReactElement | null

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  )
}

export default CurrentUserProvider
